import { FC } from "react";

import { FormTitleFontStyle, parseFormTitleFontStyle, TicketingFormCategory } from "@simplyk/common";
import { useForm } from "react-hook-form";

import { LiveFormEditorV2StepTitleStateless, StepTitleFormData } from "./LiveFormEditorV2StepTitle.stateless";

import { AmplitudeEvents } from "@/common/constants/amplitude";
import { trpc } from "@/common/helpers/trpc";
import { useTranslate } from "@/common/hooks/useTranslate";
import { useLiveFormAmplitudeEvents } from "@/features/LiveFormEditor/common/amplitude/useLiveFormAmplitudeEvents";
import { useLiveFormEditorContext } from "@/features/LiveFormEditor/common/LiveFormEditorContext";
import { usePrefilledTitle } from "@/features/LiveFormEditor/common/prefillDataBuilders/useBuildPrefilledTitle";
import { useUpdateTicketingDataInPreview } from "@/features/LiveFormEditor/common/useUpdateLiveTicketingPreview/useUpdateTicketingDataInPreview";
import { useFormSaveAndNavigate } from "@/features/LiveFormEditor/Steps/useFormSaveAndNavigate";
import { LiveTicketingData } from "@/features/LiveFormEditor/types/LiveFormEditor.types";
import { isSweepstakesCategory } from "@/common/helpers/sweepstakes";

export const TicketingLiveEditorStepTitle: FC = () => {
  const {
    apiFormObject: apiTicketing,
    formCategory,
    stepProgress,
    editOrCreateMode,
    goToPreviousStep,
    featureFlags: { isDelightfulPagesActive, isCampaignLifeCycleStepOn },
  } = useLiveFormEditorContext<"ticketing">();

  const { t } = useTranslate();
  const { logUserActionEvent } = useLiveFormAmplitudeEvents();

  const localizedTicketingField = apiTicketing.ticketingFields?.find((field) => field.locale === apiTicketing.locale);
  const initialTitle = localizedTicketingField?.title || "";
  const isSweepstakes = isSweepstakesCategory(apiTicketing.formCategory);

  const defaultValues: StepTitleFormData = {
    title: initialTitle,
    titleFontStyle: parseFormTitleFontStyle(apiTicketing.titleFontStyle) ?? FormTitleFontStyle.Classic,
  };
  const form = useForm<StepTitleFormData>({ defaultValues });

  const prefilledTitle = usePrefilledTitle(apiTicketing.formCategory);

  const updateDataInPreview = (formData: StepTitleFormData, liveDataToMutate: LiveTicketingData) => {
    const titleInPreview = formData.title || prefilledTitle;
    liveDataToMutate.ticketing.ticketingFields![0].title = titleInPreview;
    liveDataToMutate.ticketing.titleFontStyle =
      formData.titleFontStyle === FormTitleFontStyle.Classic ? null : formData.titleFontStyle;
  };
  useUpdateTicketingDataInPreview({
    form,
    mutate: (formData, liveDataToMutate) => {
      updateDataInPreview(formData, liveDataToMutate);
    },
  });

  const updateTicketingTitle = trpc.updateTicketingTitle.useMutation();
  const saveData = async (formData: StepTitleFormData) => {
    return await updateTicketingTitle.mutateAsync({
      ticketingId: apiTicketing.id,
      title: formData.title,
      titleFontStyle: formData.titleFontStyle === FormTitleFontStyle.Classic ? null : formData.titleFontStyle,
    });
  };

  const generateDescriptionWithAIMutation = trpc.startDescriptionGenerationWithAI.useMutation();

  const { handleSaveAndNext } = useFormSaveAndNavigate<StepTitleFormData>({
    type: "ticketing",
    form,
    saveData,
    getSubmitTrackingProperties: (formData) => ({
      title_font_style: formData.titleFontStyle === FormTitleFontStyle.Classic ? null : formData.titleFontStyle,
    }),
    onFirstNextCompleted: () => {
      if (localizedTicketingField?.description) {
        return;
      }
      generateDescriptionWithAIMutation.mutateAsync({
        formId: apiTicketing.id,
        title: form.getValues("title"),
        formCategory,
        locale: apiTicketing.locale,
      });
    },
  });

  const { mutateAsync: deleteTicketing } = trpc.deleteTicketing.useMutation();
  const handleDeleteDraftForm = async () => {
    await deleteTicketing({
      ticketingId: apiTicketing.id,
      onlyDraft: true,
    });
  };

  const getStepTitle = (category: TicketingFormCategory): string => {
    switch (category) {
      case TicketingFormCategory.Event:
        return t("dashboard", "liveFormEditor.stepTitle.ticketing.event.title");
      case TicketingFormCategory.Shop:
        return t("dashboard", "liveFormEditor.stepTitle.ticketing.shop.title");
      case TicketingFormCategory.Auction:
        return t("dashboard", "liveFormEditor.stepTitle.ticketing.auction.title");
      case TicketingFormCategory.Lottery:
        return t("dashboard", "liveFormEditor.stepTitle.ticketing.lottery.title");
      case TicketingFormCategory.Sweepstakes:
        return t("dashboard", "liveFormEditor.stepTitle.ticketing.sweepstakes.title");
      case TicketingFormCategory.MembershipV2:
        return t("dashboard", "liveFormEditor.stepTitle.ticketing.membership.title");
      case TicketingFormCategory.Custom:
      default:
        return t("dashboard", "liveFormEditor.stepTitle.ticketing.custom.title");
    }
  };

  return (
    <LiveFormEditorV2StepTitleStateless
      form={form}
      handleSaveAndNext={handleSaveAndNext}
      wording={{
        stepTitle: getStepTitle(formCategory),
      }}
      formId={apiTicketing.id}
      onDeleteDraftForm={handleDeleteDraftForm}
      furthestStepReached={stepProgress.reached}
      showBackButton={editOrCreateMode === "create"}
      onBackToPreviousStep={isCampaignLifeCycleStepOn ? goToPreviousStep : undefined}
      showFontStyleSelector={isDelightfulPagesActive}
      trackOnFontStyleChange={(fontStyle) =>
        logUserActionEvent(AmplitudeEvents.FormFontStyleChanged, { title_font_style: fontStyle })
      }
      forbiddenWordMessage={isSweepstakes ? t("dashboard", "sweepstakes.validation.raffleWord.title") : undefined}
    />
  );
};
